import { useQueries } from '@tanstack/react-query';
import { getCellRules } from 'api/cellRules';
import { CellRulesPanelSelect } from 'components/atoms/CellRulesPanelSelect';
import { CellRulePanelWithRules } from 'interfaces/cellRule';
import { CellRules } from 'interfaces/jobs/multiplex/cellTypingParams';
import { isEqual, map, some } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { cellRuleQueryKey } from 'utils/queryHooks/cellRule/useCellRules';
import useCellRulePanels from 'utils/queryHooks/cellRule/useCellRulePanels';
import useCellRules from 'utils/queryHooks/cellRule/useCellRules';
import { convertToCellRules } from './helpers';

interface CellRulesFromPanelSelectProps {
  studyId: string;
  selectedPanelId?: string | null;
  onSelectPanel: (panelId: string | null) => void;
  showRequiredError?: boolean;
  onCellRulesChange: (cellRules: CellRules | null) => void;
  autoAssignLastPanel?: boolean;
  restoredCellRules?: CellRules | null;
}

const CellRulesFromPanelSelect: React.FC<CellRulesFromPanelSelectProps> = ({
  studyId,
  selectedPanelId,
  onSelectPanel,
  showRequiredError,
  onCellRulesChange,
  autoAssignLastPanel,
  restoredCellRules,
}) => {
  const isRestoringPanelSelection = selectedPanelId === undefined && Boolean(restoredCellRules);

  const { data: panels, isLoading: isLoadingPanels } = useCellRulePanels(studyId, {
    enabled: isRestoringPanelSelection,
  });

  const restoredPanelQueries = useQueries({
    queries: map(panels ?? [], (candidatePanel) => ({
      queryKey: [...cellRuleQueryKey, candidatePanel.id, studyId],
      queryFn: ({ signal }: { signal?: AbortSignal }) => getCellRules(candidatePanel.id, studyId, signal),
      enabled: isRestoringPanelSelection,
      retry: false,
    })),
  });

  const matchingRestoredPanel = useMemo(() => {
    if (!isRestoringPanelSelection || !restoredCellRules) {
      return null;
    }

    const matchingQuery = restoredPanelQueries.find((query) => {
      const panelWithRules = query.data as CellRulePanelWithRules | undefined;
      return panelWithRules && isEqual(convertToCellRules(panelWithRules), restoredCellRules);
    });

    return (matchingQuery?.data as CellRulePanelWithRules | undefined) ?? null;
  }, [isRestoringPanelSelection, restoredCellRules, restoredPanelQueries]);

  useEffect(() => {
    if (selectedPanelId === undefined && matchingRestoredPanel?.id) {
      onSelectPanel(matchingRestoredPanel.id);
    }
  }, [matchingRestoredPanel, onSelectPanel, selectedPanelId]);

  const effectiveSelectedPanelId = selectedPanelId === undefined ? matchingRestoredPanel?.id ?? null : selectedPanelId;

  const { data: selectedPanel, isLoading: isLoadingSelectedPanel } = useCellRules(
    effectiveSelectedPanelId || '',
    studyId
  );

  const resolvedPanel = selectedPanelId === undefined ? matchingRestoredPanel : selectedPanel;
  const isResolvingRestoredPanel =
    isRestoringPanelSelection &&
    (isLoadingPanels || some(restoredPanelQueries, (query) => query.isLoading || query.isFetching));

  const resolvedCellRules = useMemo<CellRules | null | undefined>(() => {
    if (effectiveSelectedPanelId) {
      return resolvedPanel ? convertToCellRules(resolvedPanel) : undefined;
    }

    if (selectedPanelId === null) {
      return null;
    }

    return undefined;
  }, [effectiveSelectedPanelId, resolvedPanel, selectedPanelId]);

  const handleSelectPanel = useCallback(
    (panelId: string) => {
      onSelectPanel(panelId || null);
    },
    [onSelectPanel]
  );

  useEffect(() => {
    if (resolvedCellRules !== undefined) {
      onCellRulesChange(resolvedCellRules);
    }
  }, [onCellRulesChange, resolvedCellRules]);

  return (
    <CellRulesPanelSelect
      selectedPanelId={effectiveSelectedPanelId}
      onSelectPanel={handleSelectPanel}
      studyId={studyId}
      isRequired
      showRequiredError={showRequiredError && !isResolvingRestoredPanel && !isLoadingSelectedPanel}
      autoAssignLastPanel={Boolean(autoAssignLastPanel && selectedPanelId === undefined && !restoredCellRules)}
    />
  );
};

export default CellRulesFromPanelSelect;
